import type { AcceptanceResult, IterationStatus, LoopDetail, LoopIteration, LoopListItem, LoopStatus, OutcomeStatus, ShipAttempt, ShipStage, ShipStatus } from "../types";

/** How a state reads at a glance: waiting for the person, under way, fine, broken, needing a look, or over. */
export type Tone = "act" | "run" | "ok" | "err" | "warn" | "dim";

export const LOOP_STATUS_LABEL: Record<LoopStatus, string> = {
	running: "运行中",
	awaiting_review: "待确认",
	waiting_quota: "额度已用尽",
	passed: "已通过",
	failed: "失败",
	aborted: "已中止",
	interrupted: "已中断",
};

export const ITERATION_STATUS_LABEL: Record<IterationStatus, string> = {
	running: "运行中",
	passed: "已通过",
	failed: "未通过",
	error: "出错",
	rejected: "已拒绝",
	aborted: "已中止",
	awaiting_review: "待确认",
	waiting_quota: "额度已用尽",
};

export const SHIP_STATUS_LABEL: Record<ShipStatus, string> = {
	running: "发布中",
	refused: "已拒绝",
	failed: "失败",
	interrupted: "已中断",
	pushed: "已推送 · 待批准",
	pending: "待部署",
	deployed: "已部署",
	deploy_failed: "部署失败",
};

export const OUTCOME_STATUS_LABEL: Record<OutcomeStatus, string> = {
	complete: "已完成",
	unfinished: "未完成",
	blocked: "受阻",
};

export const SHIP_STAGE_LABEL: Record<ShipStage, string> = {
	preflight: "预检",
	review: "审查删除",
	integrate: "合入最新 main",
	check: "检查",
	push: "推送",
	deploy: "部署",
};

const LOOP_TONE: Record<LoopStatus, Tone> = {
	running: "run",
	awaiting_review: "act",
	waiting_quota: "run",
	passed: "ok",
	failed: "err",
	aborted: "dim",
	interrupted: "warn",
};

export const ITERATION_TONE: Record<IterationStatus, Tone> = {
	running: "run",
	passed: "ok",
	failed: "err",
	error: "err",
	rejected: "warn",
	aborted: "dim",
	awaiting_review: "act",
	waiting_quota: "run",
};

export const OUTCOME_TONE: Record<OutcomeStatus, Tone> = {
	complete: "ok",
	unfinished: "warn",
	blocked: "err",
};

export const SHIP_TONE: Record<ShipStatus, Tone> = {
	running: "run",
	refused: "err",
	failed: "err",
	interrupted: "warn",
	pushed: "act",
	pending: "run",
	deployed: "ok",
	deploy_failed: "err",
};

/** A ship attempt's status as a loop's pill, where it stands for the loop as a whole. */
const SHIP_PILL: Record<ShipStatus, string> = {
	running: "发布中",
	refused: "已拒绝发布",
	failed: "发布失败",
	interrupted: "发布已中断",
	pushed: "待批准部署",
	pending: "待部署",
	deployed: "已部署",
	deploy_failed: "部署失败",
};

/** "这花了多久" is the question a duration is read with; 4800s is not an answer to it. */
export function took(seconds: number): string {
	if (seconds < 60) return `${seconds} 秒`;
	const m = Math.round(seconds / 60);
	if (m < 60) return `${m} 分钟`;
	const h = Math.floor(m / 60);
	const rest = m % 60;
	return rest ? `${h} 小时 ${rest} 分` : `${h} 小时`;
}

const pad = (n: number) => String(n).padStart(2, "0");
const hm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const dayOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

function date(d: Date, now: Date): string {
	return d.getFullYear() === now.getFullYear() ? `${d.getMonth() + 1} 月 ${d.getDate()} 日` : `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
}

/** How long ago, as a list reads it: 刚刚, 5 分钟前, 3 小时前, 昨天 09:05, 9 月 21 日. */
export function when(at: number, now: number): string {
	const d = new Date(at);
	const n = new Date(now);
	const minutes = Math.floor((now - at) / 60_000);
	if (minutes < 1) return "刚刚";
	if (minutes < 60) return `${minutes} 分钟前`;
	const days = Math.round((dayOf(n) - dayOf(d)) / 86_400_000);
	if (days === 0) return `${Math.floor(minutes / 60)} 小时前`;
	if (days === 1) return `昨天 ${hm(d)}`;
	return date(d, n);
}

/** A moment as a timeline reads it: 今天 09:05, 昨天 09:05, 9 月 21 日 02:10. */
export function clock(at: number, now: number): string {
	const d = new Date(at);
	const n = new Date(now);
	const days = Math.round((dayOf(n) - dayOf(d)) / 86_400_000);
	if (days === 0) return `今天 ${hm(d)}`;
	if (days === 1) return `昨天 ${hm(d)}`;
	return `${date(d, n)} ${hm(d)}`;
}

/** The time of day alone when it is today. */
function later(at: number, now: number): string {
	return dayOf(new Date(at)) === dayOf(new Date(now)) ? hm(new Date(at)) : clock(at, now);
}

/** 第 2 轮 for a loop, 第 2 次运行 for a one-shot. */
export function runName(oneShot: boolean, index: number): string {
	return oneShot ? `第 ${index} 次运行` : `第 ${index} 轮`;
}

const restarted = (reason: string | null) => reason === "server restarted";
const capped = (reason: string | null) => reason?.startsWith("max_iterations") ?? false;
/** The reason the runner gives a failed check, which the failing check itself already shows. */
const checkFailed = (reason: string | null) => /^the check `.*` failed$/.test(reason ?? "");

export interface ListGlance {
	group: "act" | "run" | "done";
	tone: Tone;
	pill: string;
	line: string;
}

/** Where a loop stands in the list: its group, pill and one line. A passed loop stands where its newest ship attempt does. */
export function listGlance(item: LoopListItem, now: number): ListGlance {
	const it = item.latest;
	const name = it ? runName(item.oneShot, it.index) : "";
	const shipping = item.status === "passed" ? item.ship : null;
	const tone = shipping ? SHIP_TONE[shipping.status] : LOOP_TONE[item.status];
	const pill = shipping ? SHIP_PILL[shipping.status] : LOOP_STATUS_LABEL[item.status];
	const group = tone === "act" || item.status === "interrupted" ? "act" : tone === "run" ? "run" : "done";
	return { group, tone, pill, line: shipping ? shipLine(shipping.status, shipping.stage) : loopLine(item, name, now) };
}

function shipLine(status: ShipStatus, stage: ShipStage): string {
	switch (status) {
		case "running":
			return `发布中：${SHIP_STAGE_LABEL[stage]}`;
		case "pushed":
			return "已推送到 main · 待批准部署";
		case "pending":
			return "待部署";
		case "deployed":
			return "已部署";
		case "deploy_failed":
			return "部署失败";
		default:
			return `发布停止于「${SHIP_STAGE_LABEL[stage]}」`;
	}
}

function loopLine(item: LoopListItem, name: string, now: number): string {
	const it = item.latest;
	switch (item.status) {
		case "running":
			return it ? `${name}进行中 · ${took(Math.round((now - it.startedAt) / 1000))}` : "启动中…";
		case "waiting_quota":
			return item.quotaRetryAt ? `额度已用尽 · ${later(item.quotaRetryAt, now)} 恢复` : "额度已用尽";
		case "awaiting_review":
			return `${name}待确认`;
		case "interrupted":
			return restarted(item.reason) ? `${name}已中断 · 服务端重启` : `${name}已中断`;
		case "passed":
			return it ? `${name}已通过` : "已通过";
		case "aborted":
			return "已中止";
		case "failed":
			if (capped(item.reason)) return `已达 ${item.maxIterations} 轮上限，未通过`;
			if (it?.failed) return `${it.failed} 未通过`;
			if (it?.status === "error") return `${name}出错`;
			return it ? `${name}未通过` : "启动失败";
	}
}

export type NowAction = "review" | "deploy" | "abort" | "resume";

/** Where a loop stands now, with what the person can do about it and, when it failed, the check that did. */
export interface LoopNow {
	tone: Tone;
	pill: string;
	text: string;
	actions: NowAction[];
	failed?: AcceptanceResult;
	hint?: string;
}

export function loopNow(loop: LoopDetail, now: number): LoopNow {
	const last = loop.iterations.at(-1);
	const name = last ? runName(loop.oneShot, last.index) : "";
	const ship = loop.status === "passed" ? loop.ships[0] : undefined;
	if (ship) return shipNow(ship);
	const base = { tone: LOOP_TONE[loop.status], pill: LOOP_STATUS_LABEL[loop.status] };
	switch (loop.status) {
		case "running":
			return { ...base, text: last ? `${name}进行中 · ${took(Math.round((now - last.startedAt) / 1000))}` : "启动中…", actions: ["abort"] };
		case "waiting_quota":
			return { ...base, text: `Codex 额度已用尽${loop.quotaRetryAt ? ` · ${later(loop.quotaRetryAt, now)} 恢复` : ""}`, actions: ["abort"] };
		case "awaiting_review":
			return { ...base, text: `${name}命令验收已通过 · 待确认`, actions: ["review", "abort"] };
		case "interrupted": {
			// An interrupted bound loop holds its worktree until resumed or aborted; a one-shot is followed up from dev's conversation.
			const actions: NowAction[] = [];
			if (!loop.oneShot) actions.push("resume");
			if (loop.workspace !== null) actions.push("abort");
			return { ...base, text: restarted(loop.reason) ? `${name}已中断 · 服务端重启` : `${name}已中断`, actions };
		}
		case "passed":
			return { ...base, text: loop.planOnly ? "任务清单已生成" : last ? `${name}已通过` : "已完成", actions: [] };
		case "aborted":
			return { ...base, text: "已中止", actions: [] };
		case "failed": {
			const failed = last?.acceptance.find((r) => !r.passed);
			const text = capped(loop.reason) ? `已达 ${loop.maxIterations} 轮上限，未通过` : !last ? "启动失败" : last.status === "error" ? `${name}出错` : `${name}验收未通过`;
			const hint = loop.reason && !checkFailed(loop.reason) && !capped(loop.reason) ? loop.reason : undefined;
			return { ...base, text, actions: [], ...(failed ? { failed } : {}), ...(hint ? { hint } : {}) };
		}
	}
}

function shipNow(ship: ShipAttempt): LoopNow {
	const base = { tone: SHIP_TONE[ship.status], pill: SHIP_PILL[ship.status] };
	switch (ship.status) {
		case "running":
			return { ...base, text: `发布中：${SHIP_STAGE_LABEL[ship.stage]}`, actions: [] };
		case "pushed":
			return { ...base, text: `已推送到 main（${(ship.pushedSha ?? "").slice(0, 8)}）· 待批准部署`, actions: ["deploy"] };
		case "pending":
			return { ...base, text: "已请求部署 · 待部署完成", actions: [] };
		case "deployed":
			return { ...base, text: "已部署", actions: [] };
		case "deploy_failed":
			return { ...base, text: "部署失败", actions: [], ...(ship.reason ? { hint: ship.reason } : {}) };
		default: {
			const failed = ship.checks.find((r) => !r.passed);
			return { ...base, text: `发布停止于「${SHIP_STAGE_LABEL[ship.stage]}」`, actions: [], ...(failed ? { failed } : {}), ...(ship.reason ? { hint: ship.reason } : {}) };
		}
	}
}

/** One entry of a loop's timeline: a ship attempt, numbered from the first, or an iteration. */
export type TimelineEvent = { kind: "ship"; at: number; ship: ShipAttempt; number: number } | { kind: "iteration"; at: number; iteration: LoopIteration };

/** A loop's ship attempts and iterations together, newest first. */
export function timeline(loop: LoopDetail): TimelineEvent[] {
	const ships = [...loop.ships].sort((a, b) => a.createdAt - b.createdAt);
	const events: TimelineEvent[] = [
		...loop.iterations.map((iteration) => ({ kind: "iteration" as const, at: iteration.startedAt, iteration })),
		...ships.map((ship, i) => ({ kind: "ship" as const, at: ship.createdAt, ship, number: i + 1 })),
	];
	return events.sort((a, b) => b.at - a.at);
}

export interface DiffFile {
	path: string;
	added: number;
	removed: number;
	text: string;
}

/** A unified diff by file, each with the lines it added and removed. */
export function splitDiff(diff: string): DiffFile[] {
	const files: DiffFile[] = [];
	let current: (DiffFile & { lines: string[]; inHunk: boolean }) | null = null;
	const close = () => {
		if (current) files.push({ path: current.path, added: current.added, removed: current.removed, text: current.lines.join("\n") });
	};
	for (const line of diff.split("\n")) {
		const head = /^diff --git a\/(.+?) b\/(.+)$/.exec(line);
		if (head) {
			close();
			current = { path: head[2]!, added: 0, removed: 0, text: "", lines: [line], inHunk: false };
			continue;
		}
		if (!current) continue;
		current.lines.push(line);
		if (line.startsWith("@@")) current.inHunk = true;
		else if (current.inHunk && line.startsWith("+")) current.added++;
		else if (current.inHunk && line.startsWith("-")) current.removed++;
	}
	close();
	return files;
}
